/**
 * Green & LEED — the screen over the green engine: the inventory and the
 * intensities, the ACA ladder as a checklist, the trajectory against the
 * airport's own target, the peers, the plan of measures, the anomalies, and
 * a LEED self-assessment beside it.
 */
import { useMemo, useState } from "react";
import { useSession } from "../../app/SessionContext";
import {
  GREEN_MODEL_PROMPT,
  materialForGreenModel,
  trajectoryStateLabel,
  type Figure,
  type GreenEntered,
  type GreenOutput,
} from "../../domain/green";
import { money } from "../../domain/intelligence";
import { LEED_CATEGORIES, LEED_LEVELS, leedStanding, type LeedEntered } from "../../domain/leed";
import { Button, Card, ErrorState, Field, Input, Skeleton } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { useEntered } from "../executive/store";
import { AskCard, Refs, Tiles, x as s, type Figure as Tile } from "../executive/shared";
import { AcaRequirementRow } from "../entryForms/AcaChecklist";
import { useGreen, type GreenEnteredNote, type SeriesSource } from "./useGreen";
import { Link } from "react-router-dom";
import { useScreenContext } from "../../assistant/screen";
import { t, tn } from "../../i18n";

const CUR = "SAR";

/** The board's first line: where the airport stands, with its Scope 1 and 2 tonnes. */
function standsLine(site: string, out: GreenOutput): string {
  const level = t(out.aca.label);
  const tonnes = out.inventory.total.value;
  const starting = out.inventory.total.onStartingFactor;
  if (tonnes == null)
    return starting
      ? t("{site} stands at {level} with no tCO₂e Scope 1 and 2 on starting factors.", { site, level })
      : t("{site} stands at {level} with no tCO₂e Scope 1 and 2.", { site, level });
  return starting
    ? t("{site} stands at {level} with {tonnes} tCO₂e Scope 1 and 2 on starting factors.", { site, level, tonnes })
    : t("{site} stands at {level} with {tonnes} tCO₂e Scope 1 and 2.", { site, level, tonnes });
}

function trajectoryLine(out: GreenOutput): string {
  return t("The trajectory is {state}: {working}.", {
    state: trajectoryStateLabel(out.trajectory.state),
    working: out.trajectory.working,
  });
}

function tile(key: string, label: string, f: Figure, tone?: Tile["tone"]): Tile {
  /* the engine's sources are sentences; the chip shows the head of each and the whole on hover */
  const chip = (x: string) => ({
    kind: x.startsWith("KPI")
      ? ("kpi" as const)
      : /meter/.test(x)
        ? ("ledger" as const)
        : x.startsWith("entered")
          ? ("entered" as const)
          : ("rate_card" as const),
    ref: x.split(/[:—]/)[0].trim().slice(0, 24),
    label: x,
  });
  return {
    key,
    label,
    value: f.value,
    unit: f.unit,
    text: f.working,
    assumed: f.onStartingFactor,
    tone,
    sources: f.sources.map(chip),
  };
}

/** Which of the two a series is — the airport's meters or the demonstration — and how complete. */
function SeriesSourceLine({ label, src, testId }: { label: string; src: SeriesSource; testId: string }) {
  const r = src.reporting;
  return (
    <div data-testid={testId}>
      <b>{label}:</b>{" "}
      {src.kind === "meters" ? t("from the airport's meters") : t("demonstration series — no meter readings yet")}
      {r &&
        (r.total === 0
          ? ` · ${t("no meters on the register")}`
          : r.live < r.total
            ? ` · ${t("partial: {live} of {total} meters reporting", { live: r.live, total: r.total })}`
            : ` · ${t("{live} of {total} meters reporting", { live: r.live, total: r.total })}`)}
      {src.incomersOnly && ` · ${t("incomers only, so sub-meters are not counted twice")}`}
      {src.kind === "meters" && src.origins.length > 0 && ` · ${t("readings from {origins}", { origins: src.origins.join(", ") })}`}
    </div>
  );
}

export function GreenPage() {
  const session = useSession();
  const g = useGreen();
  const leed = useEntered<LeedEntered>("executive.leed", {});
  const standing = useMemo(() => leedStanding(leed.value), [leed.value]);
  const out = g.out;
  useScreenContext(
    out
      ? {
          route: "/green-program",
          title: "Green & LEED",
          summary: [standsLine(session.site.code, out), trajectoryLine(out), out.plan.note],
          material: materialForGreenModel(out, session.site.code),
          figures: out.figures,
          own: (q) => answerGreen(q, out),
          prompt: GREEN_MODEL_PROMPT,
          offers: [
            { label: "The next ACA level", ask: "what does the next ACA level need?" },
            { label: "Cheapest per tonne", ask: "which measure is cheapest per tonne?" },
            { label: "What is missing?", ask: "what inputs are missing?" },
          ],
        }
      : null,
  );
  return (
    <Page
      eyebrow={t("{site} · Executive", { site: session.site.code })}
      title="Green & LEED"
      actions={
        <Button variant="primary" onClick={() => window.print()}>
          {t("Print")}
        </Button>
      }
    >
      <p className={s.lead}>
        {t(
          "The airport's carbon footprint, its reduction plan, and how it compares with peers.",
        )}
      </p>
      {g.pending && <Skeleton rows={8} />}
      {!g.pending && !out && <ErrorState error={new Error(t("the green program could not be built"))} />}
      {out && (
        <div className={s.page}>
          {g.demo && (
            <p className={s.muted}>
              <b>{t("Demonstration meter series.")}</b>{" "}
              {t(
                "No meter readings yet, so these are sample figures.",
              )}
            </p>
          )}
          {/* build 7: no hidden passenger count — say what the per-passenger figures wait for */}
          {g.inputs && g.inputs.entered.passengersPerYear == null && (
            <p className={s.muted} data-testid="green-no-passengers">
              <b>{t("No passenger count yet.")}</b>{" "}
              {t("Per-passenger figures stay blank until the yearly passengers are entered below.")}
            </p>
          )}
          {g.sources && (
            <div className={s.muted} data-testid="green-sources">
              <SeriesSourceLine label={t("Electricity")} src={g.sources.electricity} testId="green-source-electricity" />
              <SeriesSourceLine label={t("Water")} src={g.sources.water} testId="green-source-water" />
              <Link to="/meters">{t("Meter register →")}</Link>
            </div>
          )}
          <Tiles
            items={[
              tile("total", t("Scope 1 + 2"), out.inventory.total, "warn"),
              tile("scope2", t("Scope 2, electricity"), out.inventory.scope2),
              tile("scope1", t("Scope 1, fuel"), out.inventory.scope1),
              tile("kgPax", t("carbon a passenger"), out.intensity.kgPerPax),
              tile("kwhPax", t("energy a passenger"), out.intensity.kwhPerPax),
              tile("renew", t("renewable share"), out.intensity.renewablePct),
              {
                key: "aca",
                label: t("accreditation"),
                value: out.aca.level || null,
                unit: "",
                text: out.aca.nextLevel
                  ? `${t(out.aca.label)}; ${tn(out.aca.nextTodo.length, "next level needs {n} thing", "next level needs {n} things")}`
                  : t(out.aca.label),
                tone: out.aca.level >= 2 ? "good" : "warn",
                sources: [],
              },
              {
                key: "traj",
                label: t("trajectory"),
                value: out.trajectory.gapPct,
                unit: "%",
                text: t("{state} — {working}", {
                  state: trajectoryStateLabel(out.trajectory.state),
                  working: out.trajectory.working,
                }),
                tone:
                  out.trajectory.state === "behind"
                    ? "bad"
                    : out.trajectory.state === "no_target" || out.trajectory.state === "no_data"
                      ? "warn"
                      : "good",
                sources: [],
              },
              {
                key: "leed",
                label: t("LEED self-assessment"),
                value: standing.total,
                unit: t("of {max}", { max: standing.max }),
                text: standing.level
                  ? standing.nextLevel
                    ? t("{level}; {needs} more for {next}", {
                        level: t(standing.level),
                        needs: standing.nextLevel.needs,
                        next: t(standing.nextLevel.label),
                      })
                    : t(standing.level)
                  : t("not yet Certified; {n} points to go", { n: standing.nextLevel?.needs ?? 40 }),
                assumed: true,
                tone: standing.level ? "good" : "warn",
                sources: [],
              },
            ]}
            currency={CUR}
          />
          {(g.note.savedTo || leed.savedTo) && <p className={s.saved}>{g.note.savedTo ?? leed.savedTo}</p>}
          <div className={s.two}>
            <Card
              title="Airport Carbon Accreditation"
              sub={t("{level}; each requirement computed, documented or entered", { level: t(out.aca.label) })}
            >
              <div className={s.tableWrap}>
                <table className={s.table}>
                  <tbody>
                    {out.aca.requirements.map((r) => (
                      /* build 6B: the entered and documented requirements can be recorded here */
                      <AcaRequirementRow
                        key={r.key}
                        r={r}
                        item={g.note.value.checklist?.[r.key]}
                        checklist={g.note.value.checklist}
                        ready={!g.note.loading}
                        onSave={(checklist) => g.note.save({ ...g.note.value, checklist })}
                      >
                        <td className={s.num}>L{r.level}</td>
                        <td>
                          <span className={r.met ? s.tagGreen : s.tagRed}>{r.met ? t("met") : t("gap")}</span>
                        </td>
                        <td>
                          <b>{t(r.text)}</b>
                          <div className={s.muted}>
                            {r.basis}
                            {r.todo && !r.met ? ` — ${r.todo}` : ""}
                          </div>
                        </td>
                      </AcaRequirementRow>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            <div className={s.page}>
              <Card title="The plan" sub={out.plan.note}>
                {out.plan.steps.length === 0 && (
                  <p className={s.muted}>
                    {t(
                      "No measure can be sized yet: enter the lighting and HVAC shares, turnarounds, the fleet's diesel share and the waste tonnes in the drawer.",
                    )}
                  </p>
                )}
                <div className={s.tableWrap}>
                  <table className={s.table}>
                    <tbody>
                      {out.plan.steps.map((st) => (
                        <tr key={st.key}>
                          <td>
                            <b>{st.label}</b>
                            <div className={s.muted}>{st.working}</div>
                          </td>
                          <td className={s.num}>{st.tonnesPerYear} t/yr</td>
                          <td className={s.num}>{money(st.costNow, CUR)}</td>
                          <td className={s.num}>
                            {money(st.costPerTonne, CUR)}/t
                            {st.onStartingFactor && <div className={s.tag}>{t("starting factor")}</div>}
                          </td>
                          <td className={s.num}>{st.year}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              <Card title="Anomalies" sub="Months 15% above last year">
                {out.anomalies.length === 0 && (
                  <p className={s.muted}>
                    {t(
                      "None: no month is more than 15% above the same month last year, or there is not yet a year to compare against.",
                    )}
                  </p>
                )}
                <ul className={s.plain}>
                  {out.anomalies.map((a) => (
                    <li key={`${a.meter}${a.month}`}>
                      {t("{meter} {month}: {value} against {lastYear} last year (+{pct}%)", {
                        meter: a.meter === "electricity" ? t("electricity") : t("water"),
                        month: a.month,
                        value: a.value.toLocaleString("en"),
                        lastYear: a.lastYear.toLocaleString("en"),
                        pct: a.pct,
                      })}
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
          <Card title="Peers" sub={out.peers.note}>
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t("regional")}</th>
                    <th>kgCO₂e/pax</th>
                    <th>ACA</th>
                    <th>#</th>
                    <th>{t("global")}</th>
                    <th>kgCO₂e/pax</th>
                    <th>ACA</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: Math.max(out.peers.regional.length, out.peers.global.length) }).map((_, i) => {
                    const r = out.peers.regional[i];
                    const gl = out.peers.global[i];
                    return (
                      <tr key={i}>
                        <td className={s.num}>{r ? i + 1 : ""}</td>
                        <td className={r?.isSelf ? s.self : ""}>{r?.name ?? ""}</td>
                        <td className={s.num}>
                          {r ? (r.kgPerPax ?? <span className={s.blank}>{t("blank")}</span>) : ""}
                        </td>
                        <td className={s.num}>{r ? (r.acaLevel ?? "—") : ""}</td>
                        <td className={s.num}>{gl ? i + 1 : ""}</td>
                        <td className={gl?.isSelf ? s.self : ""}>{gl?.name ?? ""}</td>
                        <td className={s.num}>
                          {gl ? (gl.kgPerPax ?? <span className={s.blank}>{t("blank")}</span>) : ""}
                        </td>
                        <td className={s.num}>{gl ? (gl.acaLevel ?? "—") : ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
          <LeedCard key={leed.at ?? "seed"} standing={standing} entered={leed.value} onSave={(e) => void leed.save(e)} />
          <AskCard
            title="The line for the board"
            sub="Summary for the board, from the airport's own figures"
            board={[
              standsLine(session.site.code, out),
              trajectoryLine(out),
              out.missing.length
                ? t("{note} {n} inputs are missing: {fields}.", {
                    note: out.plan.note,
                    n: out.missing.length,
                    fields: out.missing.map((m) => m.field).join(", "),
                  })
                : `${out.plan.note} `,
            ]}
            material={materialForGreenModel(out, session.site.code)}
            figures={out.figures}
            own={(q) => answerGreen(q, out)}
            prompt={GREEN_MODEL_PROMPT}
            placeholder={t(
              "What does the next ACA level need? Which measure is cheapest per tonne? Why did a month jump?",
            )}
          />
          <details className={s.drawer}>
            <summary>
              {t("Figures to enter by hand")}
            </summary>
            <div className={s.drawerBody}>
              {!g.note.loading && <GreenForm key={g.note.at ?? "seed"} note={g.note.value} missing={out.missing} onSave={(n) => void g.note.save(n)} />}
            </div>
          </details>
        </div>
      )}
    </Page>
  );
}

function answerGreen(q: string, out: GreenOutput): string | null {
  const t = q.toLowerCase();
  if (/next level|aca|accredit/.test(t))
    return `${out.aca.label}. ${out.aca.nextLevel ? `Level ${out.aca.nextLevel} needs: ${out.aca.nextTodo.join("; ") || "nothing more"}.` : "Top of the ladder."}`;
  if (/cheapest|per tonne|measure|plan/.test(t))
    return (
      out.plan.steps
        .map((st, i) => `${i + 1}. ${st.label}: ${st.tonnesPerYear} t/yr at ${st.costPerTonne}/t — ${st.working}`)
        .join("\n") || "No measure can be sized yet."
    );
  if (/jump|anomal|why did/.test(t))
    return (
      out.anomalies
        .map((a) => `${a.meter} ${a.month}: ${a.value} against ${a.lastYear} last year (+${a.pct}%)`)
        .join("\n") || "No anomaly on any meter."
    );
  if (/missing|enter/.test(t))
    return out.missing.map((m) => `${m.field}: ${m.why}`).join("\n") || "Nothing is missing.";
  if (/traject|target|on track|behind/.test(t)) return out.trajectory.working;
  return null;
}

function LeedCard({
  standing,
  entered,
  onSave,
}: {
  standing: ReturnType<typeof leedStanding>;
  entered: LeedEntered;
  onSave: (e: LeedEntered) => void;
}) {
  const [pts, setPts] = useState<Record<string, string>>(
    Object.fromEntries(LEED_CATEGORIES.map((c) => [c.key, String(entered.points?.[c.key] ?? "")])),
  );
  return (
    <Card
      title="LEED self-assessment"
      sub={
        standing.nextLevel
          ? t(
              "{total} of {max} points — {level}; {needs} more for {next} · LEED v4 O+M category maxima; check the rating system version before applying",
              {
                total: standing.total,
                max: standing.max,
                level: standing.level ? t(standing.level) : t("below Certified"),
                needs: standing.nextLevel.needs,
                next: t(standing.nextLevel.label),
              },
            )
          : t(
              "{total} of {max} points — {level} · LEED v4 O+M category maxima; check the rating system version before applying",
              {
                total: standing.total,
                max: standing.max,
                level: standing.level ? t(standing.level) : t("below Certified"),
              },
            )
      }
    >
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          const points: Record<string, number> = {};
          for (const c of LEED_CATEGORIES) if (pts[c.key] !== "") points[c.key] = Number(pts[c.key]);
          onSave({ ...entered, points });
        }}
      >
        {LEED_CATEGORIES.map((c) => (
          <Field key={c.key} label={t("{category} (of {max})", { category: t(c.label), max: c.max })}>
            <Input
              type="number"
              min={0}
              max={c.max}
              value={pts[c.key]}
              onChange={(e) => setPts({ ...pts, [c.key]: e.target.value })}
              aria-label={t("{category} points", { category: t(c.label) })}
              title={t(c.informedBy)}
            />
          </Field>
        ))}
        <Button type="submit">{t("Save the assessment")}</Button>
      </form>
      <p className={s.muted}>
        {t("Levels: {levels}. Most headroom: {headroom}.", {
          levels: LEED_LEVELS.map((l) => t("{level} {min}+", { level: t(l.label), min: l.min })).join(" · "),
          headroom: standing.headroom
            .map((h) => t("{category} ({n} available)", { category: t(h.category.label), n: h.available }))
            .join("; "),
        })}
      </p>
    </Card>
  );
}

function GreenForm({
  note,
  missing,
  onSave,
}: {
  note: GreenEnteredNote;
  missing: GreenOutput["missing"];
  onSave: (n: GreenEnteredNote) => void;
}) {
  const fields: { key: keyof GreenEntered; label: string }[] = [
    { key: "passengersPerYear", label: "passengers a year" },
    { key: "dieselLitresPerYear", label: "diesel, litres a year" },
    { key: "petrolLitresPerYear", label: "petrol, litres a year" },
    { key: "renewableKwhPerYear", label: "on-site renewable kWh a year" },
    { key: "wasteTonnesPerYear", label: "waste tonnes a year" },
    { key: "wasteDivertedPct", label: "waste diverted %" },
    { key: "floorAreaM2", label: "floor area m²" },
    { key: "baselineYear", label: "baseline year" },
    { key: "baselineTonnes", label: "baseline tCO₂e" },
    { key: "targetYear", label: "target year" },
    { key: "targetReductionPct", label: "reduction target %" },
    { key: "lightingSharePct", label: "lighting share of electricity %" },
    { key: "hvacSharePct", label: "HVAC share of electricity %" },
    { key: "turnaroundsPerYear", label: "turnarounds a year" },
    { key: "fleetDieselSharePct", label: "fleet share of diesel %" },
    { key: "scope3TonnesEstimate", label: "Scope 3 estimate tCO₂e" },
  ];
  const [v, setV] = useState<Record<string, string>>(
    Object.fromEntries(fields.map((f) => [f.key, note.entered?.[f.key] != null ? String(note.entered[f.key]) : ""])),
  );
  const [grid, setGrid] = useState(note.factors?.grid != null ? String(note.factors.grid) : "");
  return (
    <Card
      title="Entered quantities and factors"
      sub={
        missing.length
          ? t("missing: {list}", { list: missing.map((m) => m.why).join("; ") })
          : "every quantity the engine needs is present"
      }
    >
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          const entered: GreenEntered = {};
          for (const f of fields) if (v[f.key] !== "") (entered as Record<string, unknown>)[f.key] = Number(v[f.key]);
          onSave({ ...note, entered, factors: { ...note.factors, grid: grid !== "" ? Number(grid) : undefined } });
        }}
      >
        {fields.map((f) => (
          <Field key={f.key} label={f.label}>
            <Input
              type="number"
              min={0}
              step="any"
              value={v[f.key]}
              onChange={(e) => setV({ ...v, [f.key]: e.target.value })}
              aria-label={t(f.label)}
            />
          </Field>
        ))}
        <Field label="grid factor kgCO₂e/kWh (the supplier's)">
          <Input
            type="number"
            min={0}
            step="any"
            value={grid}
            onChange={(e) => setGrid(e.target.value)}
            aria-label={t("grid factor")}
          />
        </Field>
        <Button type="submit" variant="primary">
          {t("Save")}
        </Button>
      </form>
      <Refs
        list={[
          {
            kind: "entered",
            ref: "entered",
            label: t("green program inputs"),
            enteredBy: note.enteredBy,
            enteredAt: note.enteredAt,
          },
        ]}
      />
    </Card>
  );
}
